/**
 * cro-core.js — shared helpers for the CRO product + collection templates.
 *
 * - Bottom sheets: native <dialog>.showModal(), one history entry per open sheet so the
 *   Android back button closes the sheet instead of leaving the page, Esc/backdrop/swipe-down.
 * - addToCart(): POST /cart/add.js with the drawer's sections, then the theme's own
 *   CartAddEvent, so the cart drawer (GoKwik checkout) and header bubble update exactly as
 *   they do for the stock product form.
 * - Quick add sheet (collection grid + PDP recommendations).
 * - What's in the cart: variant ids from the page (Liquid) kept current after adds, cart drawer
 *   changes and back/forward navigation, so a size already in the cart offers "View cart".
 * - Card warm-up: on tap, the product page's hero photo starts downloading in parallel with
 *   the page itself, and the card image is handed over as an instant placeholder.
 */
import { CartAddEvent } from '@theme/events';

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

/* ------------------------------------------------------------------ sheets */
const stack = [];
let swallowPop = 0;
let pageUrl = '';

/** history.replaceState that survives sheets: an open sheet owns the top history entry, so a URL
 *  set while it is open (a filter applied live, a size picked) is carried back onto the page's
 *  own entry when the sheet's entry is popped. */
export function replaceUrl(url) {
  pageUrl = new URL(url, location.href).href;
  try {
    history.replaceState(history.state, '', pageUrl);
  } catch (e) {}
}
function restoreUrl() {
  if (!pageUrl || pageUrl === location.href) return;
  try {
    history.replaceState(history.state, '', pageUrl);
  } catch (e) {}
}

export function openSheet(d) {
  if (!d || d.open) return;
  prepSheet(d);
  try {
    d.showModal();
  } catch (e) {
    d.setAttribute('open', '');
  }
  stack.push(d);
  try {
    history.pushState({ croSheet: d.id }, '');
  } catch (e) {}
  document.documentElement.style.overflow = 'hidden';
  d.dispatchEvent(new CustomEvent('cro:open'));
}

export function closeSheet(d, fromPop = false) {
  if (!d || !d.open) return;
  const i = stack.indexOf(d);
  if (i > -1) stack.splice(i, 1);
  if (!fromPop && history.state && history.state.croSheet === d.id) {
    swallowPop++;
    history.back();
  }
  d.classList.add('is-out');
  setTimeout(() => {
    d.classList.remove('is-out');
    d.style.transform = '';
    d.close();
    if (!stack.length) document.documentElement.style.overflow = '';
    d.dispatchEvent(new CustomEvent('cro:close'));
  }, 180);
}

export function closeAllSheets() {
  [...stack].reverse().forEach((d) => closeSheet(d));
}

// Registered when this module first runs (before SearchTap, which is lazy-loaded on these
// templates), so SearchTap's router never sees our sheet back-steps.
window.addEventListener('popstate', (e) => {
  if (swallowPop) {
    swallowPop--;
    e.stopImmediatePropagation();
    restoreUrl();
    return;
  }
  const d = stack[stack.length - 1];
  if (d) {
    e.stopImmediatePropagation();
    closeSheet(d, true);
    restoreUrl();
  }
});

function prepSheet(d) {
  if (d.dataset.croReady) return;
  d.dataset.croReady = '1';
  d.addEventListener('cancel', (e) => {
    e.preventDefault();
    closeSheet(d);
  });
  d.addEventListener('click', (e) => {
    if (e.target !== d) return;
    const r = d.getBoundingClientRect();
    if (e.clientY < r.top || e.clientY > r.bottom || e.clientX < r.left || e.clientX > r.right) closeSheet(d);
  });
  const head = d.querySelector('.cro-sheet__head');
  if (!head) return;
  let y0 = null;
  let dy = 0;
  head.addEventListener(
    'touchstart',
    (e) => {
      y0 = e.touches[0].clientY;
      dy = 0;
    },
    { passive: true }
  );
  head.addEventListener(
    'touchmove',
    (e) => {
      if (y0 === null) return;
      dy = Math.max(0, e.touches[0].clientY - y0);
      d.style.transform = `translateY(${dy}px)`;
    },
    { passive: true }
  );
  head.addEventListener('touchend', () => {
    if (y0 === null) return;
    y0 = null;
    if (dy > 70) closeSheet(d);
    else d.style.transform = '';
  });
}

/* ------------------------------------------------------------------ toast */
let toastEl;
let toastTimer;
export function toast(message, action) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'cro-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  toastEl.style.pointerEvents = 'none';
  if (action) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = action.label;
    b.style.cssText =
      'margin-left:12px;padding:0;border:0;background:none;color:#9dbcff;font:inherit;font-weight:600;text-decoration:underline;text-underline-offset:3px';
    b.addEventListener('click', () => {
      hideToast();
      action.run();
    });
    toastEl.appendChild(b);
    toastEl.style.pointerEvents = 'auto';
  }
  requestAnimationFrame(() => toastEl.classList.add('is-on'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideToast, action ? 4500 : 2600);
}
function hideToast() {
  if (toastEl) toastEl.classList.remove('is-on');
}

/* ------------------------------------------------------------------ copy */
/** Clipboard write with the old textarea fallback (in-app browsers without the async API). */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const t = document.createElement('textarea');
    t.value = text;
    t.setAttribute('readonly', '');
    t.style.cssText = 'position:fixed;top:0;opacity:0';
    document.body.appendChild(t);
    t.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_) {}
    t.remove();
    return ok;
  }
}

export async function copyCode(code) {
  const ok = await copyText(code);
  toast(ok ? `Code ${code} copied. Apply it at checkout.` : `Use code ${code} at checkout.`);
  track('cro_copy_code', { code });
}

/* ------------------------------------------------------------------ what's in the cart */
const inCart = new Set();
try {
  JSON.parse(document.getElementById('cro-cart-ids')?.textContent || '[]').forEach((id) => inCart.add(String(id)));
} catch (e) {}
export const isInCart = (id) => !!id && inCart.has(String(id));
function setCartIds(ids) {
  inCart.clear();
  ids.forEach((id) => inCart.add(String(id)));
  document.dispatchEvent(new CustomEvent('cro:cart'));
}
let ownEvent = false;
let cartTimer;
// The cart drawer (quantity change, remove) announces itself with the theme's cart:update event.
document.addEventListener('cart:update', () => {
  if (ownEvent) return;
  clearTimeout(cartTimer);
  cartTimer = setTimeout(refreshCount, 250);
});
// Back/forward can show an older copy of the page: re-read the cart.
window.addEventListener('pageshow', (e) => {
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (e.persisted || (nav && nav.type === 'back_forward')) refreshCount();
});

/* ------------------------------------------------------------------ cart */
export function openCart() {
  const drawer = $('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else if (drawer) drawer.querySelector('button')?.click();
  else location.href = '/cart';
}

export async function refreshCount() {
  try {
    const c = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    $$('#CartCount, [data-cro-cart-count]').forEach((el) => {
      el.textContent = c.item_count;
      el.dataset.n = c.item_count;
    });
    setCartIds((c.items || []).map((i) => i.variant_id));
    return c;
  } catch (e) {
    return null;
  }
}

export async function addToCart(id, { quantity = 1, openDrawer = true, productId = '' } = {}) {
  const sectionIds = [...new Set($$('cart-items-component[data-section-id]').map((e) => e.dataset.sectionId))];
  const body = { items: [{ id: Number(id), quantity }] };
  if (sectionIds.length) {
    body.sections = sectionIds.join(',');
    body.sections_url = location.pathname;
  }
  let res;
  let data;
  try {
    res = await fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    data = await res.json();
  } catch (e) {
    return { ok: false, message: 'Network issue. Please try again.' };
  }
  if (!res.ok || data.status) {
    return { ok: false, message: data.description || data.message || 'Could not add to cart. Please try again.' };
  }

  const drawer = $('cart-drawer-component');
  const auto = !!drawer && drawer.hasAttribute('auto-open');
  if (auto && !openDrawer) drawer.removeAttribute('auto-open');
  inCart.add(String(id));
  document.dispatchEvent(new CustomEvent('cro:cart'));
  ownEvent = true;
  try {
    // Same source as the stock product form, so the header bubble increments instead of resetting.
    document.body.dispatchEvent(
      new CartAddEvent({}, String(id), {
        source: 'product-form-component',
        itemCount: quantity,
        productId: String(productId),
        sections: data.sections,
      })
    );
  } catch (e) {}
  ownEvent = false;
  if (auto && !openDrawer) drawer.setAttribute('auto-open', '');
  if (openDrawer && drawer && !auto) openCart();
  refreshCount();
  track('cro_add_to_cart', { variant_id: id, product_id: productId });
  return { ok: true, data };
}

/* ------------------------------------------------------------------ quick add */
/**
 * Wires every [data-cro-qa] button inside `root` to the quick-add sheet `sheet`.
 * The button carries its variants as "id~title~available|…", so the sheet opens instantly.
 */
export function initQuickAdd(root, sheet) {
  if (!root || !sheet) return;
  const sizes = sheet.querySelector('[data-cro-qa-sizes]');
  root.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cro-qa]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = btn.closest('.cro-card');
    const variants = btn.dataset.croQa.split('|').map((s) => {
      const [id, title, a] = s.split('~');
      return { id, title, available: a === '1' };
    });
    const pid = card ? card.dataset.pid : '';
    if (variants.length === 1) {
      if (isInCart(variants[0].id)) return inCartToast();
      btn.style.opacity = '.5';
      const r = await addToCart(variants[0].id, { openDrawer: false, productId: pid });
      btn.style.opacity = '';
      addedToast(r, btn.dataset.title);
      return;
    }
    const img = card && card.querySelector('.cro-card__img');
    const qaImg = sheet.querySelector('[data-cro-qa-img]');
    if (qaImg) qaImg.src = img ? img.currentSrc || img.src : '';
    sheet.querySelector('[data-cro-qa-title]').textContent = btn.dataset.title || '';
    sheet.querySelector('[data-cro-qa-price]').textContent = btn.dataset.price || '';
    const pdp = sheet.querySelector('[data-cro-qa-pdp]');
    const link = card && card.querySelector('a[href]');
    if (pdp && link) pdp.href = link.href;
    sizes.innerHTML = '';
    variants.forEach((v) => {
      const label = document.createElement('label');
      label.className = 'cro-size';
      label.innerHTML = '<input type="radio" name="cro-qa-size"><span class="cro-size__box"></span>';
      const input = label.firstChild;
      input.value = v.id;
      input.disabled = !v.available;
      label.lastChild.textContent = v.title;
      if (isInCart(v.id)) label.classList.add('is-incart');
      sizes.appendChild(label);
    });
    openSheet(sheet);
    track('cro_quick_add_open', { product_id: pid });
    sheet.dataset.pid = pid;
    sheet.dataset.title = btn.dataset.title || '';
  });
  sizes.addEventListener('change', async (e) => {
    const input = e.target;
    if (!input.matches('input[type=radio]')) return;
    // Same size again: nothing to add, point to the cart (quantity can change there).
    if (isInCart(input.value)) {
      setTimeout(() => closeSheet(sheet), 120);
      return inCartToast(input.closest('.cro-size')?.textContent.trim());
    }
    // Optimistic: acknowledge the tap immediately, close the sheet, confirm when the cart answers.
    sizes.classList.add('is-busy');
    setTimeout(() => closeSheet(sheet), 160);
    toast('Adding to cart…');
    const r = await addToCart(input.value, { openDrawer: false, productId: sheet.dataset.pid });
    sizes.classList.remove('is-busy');
    addedToast(r, sheet.dataset.title);
  });
}

function addedToast(r, title) {
  if (r.ok) toast('Added to cart', { label: 'View cart', run: openCart });
  else toast(r.message || 'Could not add to cart');
}
export function inCartToast(what = '') {
  toast(what ? `${what} is already in your cart` : 'Already in your cart', { label: 'View cart', run: openCart });
}

/* ------------------------------------------------------------------ warm-up on card tap */
const warmed = new Set();
export function heroWidth() {
  const need = Math.min(window.innerWidth || 390, 480) * (window.devicePixelRatio || 1);
  return need <= 540 ? 540 : need <= 720 ? 720 : 900;
}
function warm(a) {
  const img = a.querySelector('img[data-base]');
  try {
    sessionStorage.setItem(
      'cro-hero',
      JSON.stringify({ u: new URL(a.href, location.href).pathname, s: img ? img.currentSrc || img.src : '' })
    );
  } catch (e) {}
  if (!img || warmed.has(a.href)) return;
  warmed.add(a.href);
  const l = document.createElement('link');
  l.rel = 'prefetch';
  l.as = 'image';
  l.href = `${img.dataset.base}&width=${heroWidth()}&quality=65`;
  document.head.appendChild(l);
}
document.addEventListener(
  'click',
  (e) => {
    const a = e.target.closest && e.target.closest('a[data-cro-card-link]');
    if (a && !e.defaultPrevented && !e.metaKey && !e.ctrlKey) warm(a);
  },
  true
);

/* ------------------------------------------------------------------ global delegates */
document.addEventListener('click', (e) => {
  const t = e.target;
  if (!t.closest) return;
  const opener = t.closest('[data-cro-open]');
  if (opener) {
    const d = document.getElementById(opener.dataset.croOpen);
    if (d) {
      e.preventDefault();
      openSheet(d);
      track('cro_sheet_open', { sheet: opener.dataset.croOpen.split('-')[0] });
    }
    return;
  }
  const closer = t.closest('[data-cro-close]');
  if (closer) {
    const d = closer.closest('dialog');
    if (d) {
      e.preventDefault();
      closeSheet(d);
    }
    return;
  }
  const copy = t.closest('[data-cro-copy]');
  if (copy) {
    e.preventDefault();
    copyCode(copy.dataset.croCopy);
    return;
  }
  // Header cart link (collection page): open the drawer instead of going to /cart
  const cartLink = t.closest('a[data-cro-cart-link]');
  if (cartLink && !e.metaKey && !e.ctrlKey) {
    e.preventDefault();
    openCart();
    track('cro_header_cart');
  }
});

/** Lets the Wishlist Club app render hearts into cards added after page load. */
export function refreshWishlist() {
  try {
    if (typeof window.$th_refreshWishlistItems === 'function') window.$th_refreshWishlistItems(true);
  } catch (e) {}
}

/** Fetch one section's HTML for a URL through the Section Rendering API. */
export async function fetchSection(url, sectionId) {
  const u = new URL(url, location.href);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(`section ${res.status}`);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}

/** Lightweight analytics hooks: GTM dataLayer + Shopify custom pixels + Clarity. */
export function track(name, data = {}) {
  try {
    (window.dataLayer = window.dataLayer || []).push({ event: name, ...data });
  } catch (e) {}
  try {
    window.Shopify && Shopify.analytics && Shopify.analytics.publish && Shopify.analytics.publish(name, data);
  } catch (e) {}
  try {
    typeof window.clarity === 'function' && window.clarity('event', name);
  } catch (e) {}
}

/** Menu = the theme's own menu drawer (the same one the bottom icon bar's ☰ opens). */
export function openMenu() {
  const t = $('.header__icon--menu.header__icon--summary');
  if (t) t.click();
  else location.href = '/collections/all';
}

/** Search = SearchTap, opened through its own #search-mobile trigger (the lazy loader in
 *  snippets/searchtap.liquid starts SearchTap on that click and replays it once ready). */
export function openSearch() {
  const t = $('.search-action #search-mobile') || $('#search-mobile');
  if (t) t.click();
  else location.href = '/search';
}
